import { FaCheckCircle, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';

const StepCard = ({ step, title, status, progress, totalQuestions, onClick, error, errorMessage }) => {
  useTheme();

  const getProgressMessage = () => {
    switch (step) {
      case 2:
        return "Post-formation documents in progress";
      case 3:
        return "Applying for EIN";
      default:
        return "";
    }
  };

  const getStepInstructions = () => {
    switch (step) {
      case 1:
        return "Complete Company Formation Checklist with all required information";
      case 2:
        return "Post Formation documents preparation after checklist confirmation";
      case 3:
        return "Tax ID (EIN) application and processing";
      default:
        return "";
    }
  };

  const progressPercent = totalQuestions > 0 ? (progress / totalQuestions) * 100 : 0;
  const progressPercentLabel = `${Math.round(progressPercent)}%`;

  const handleClick = () => {
    if (onClick) { onClick(step); }
  };

  const getStatusBadge = () => {
    if (error) return 'ACTION NEEDED';
    switch (status) {
      case 'in-progress':
        return 'IN PROGRESS';
      case 'submitted':
      case 'completed':
        return 'COMPLETED';
      default:
        return 'PENDING';
    }
  };

  const getCardStyles = () => {
    if (error) return 'bg-red-600 border-red-500';
    switch (status) {
      case 'in-progress':
        return 'bg-[#1e2a3e] border-[#2d3f5a]';
      case 'submitted':
      case 'completed':
        return 'bg-green-600 border-green-500';
      default:
        return 'bg-blue-600 border-blue-500';
    }
  };

  const getBadgeStyles = () => {
    if (error) return 'bg-red-800 text-white';
    switch (status) {
      case 'in-progress':
        return 'bg-blue-600 text-white';
      case 'submitted':
      case 'completed':
        return 'bg-green-800 text-white';
      default:
        return 'bg-blue-800 text-white';
    }
  };

  const getMessageStyles = () => {
    if (error) return 'text-red-100';
    switch (status) {
      case 'submitted':
      case 'completed':
        return 'text-green-200';
      case 'in-progress':
        return 'text-blue-300';
      default:
        return 'text-blue-200';
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative w-full p-4 sm:p-5 rounded-2xl border cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col min-h-[150px] lg:min-h-[170px] ${getCardStyles()}`}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-white font-bold text-base sm:text-lg">Step {step}</span>
        <span className={`px-2.5 sm:px-3 py-1 rounded text-[10px] sm:text-xs font-bold uppercase tracking-wide ${getBadgeStyles()}`}>
          {getStatusBadge()}
        </span>
      </div>

      <h3 className="text-white font-semibold text-sm sm:text-base leading-snug flex-grow">{title}</h3>

      {status === "in-progress" && step === 1 && !error && (
        <div className="mt-auto pt-3">
          <div className="flex items-center justify-between text-[11px] text-gray-300 mb-1">
            <span className="tracking-wide">Progress</span>
            <span className="font-semibold">{progress}/{totalQuestions} • {progressPercentLabel}</span>
          </div>
          <div className="h-1.5 bg-gray-600 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500 bg-gray-400"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      )}

      {status === "in-progress" && step !== 1 && !error && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-300">
          <FaSpinner className="w-3 h-3 animate-spin" />
          <span>{getProgressMessage()}</span>
        </div>
      )}

      {status === "pending" && !error && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-blue-100">
          <span>{getProgressMessage()}</span>
        </div>
      )}

      {(status === "submitted" || status === "completed") && !error && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-green-100">
          <FaCheckCircle className="w-3 h-3" />
          <span>Completed</span>
        </div>
      )}

      {error && (
        <div className="mt-auto pt-2 flex items-center gap-2 text-xs sm:text-sm text-red-100">
          <FaExclamationTriangle className="w-3 h-3" />
          <span>{errorMessage || "Action required on this step"}</span>
        </div>
      )}

      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-dark-800 dark:bg-dark-700 text-white text-xs sm:text-sm rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal text-center sm:text-left max-w-[min(280px,80vw)] z-10">
        <span className="block">{getStepInstructions()}</span>
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-dark-800 dark:border-t-dark-700" />
      </div>
    </div>
  );
};

export default StepCard;

